// Root admin layout — sidebar + topbar + content. Protected by session check.
// Visual port of frontend/src/app/admin/layout.tsx + admin-layout-client.tsx.
import {
  lazy,
  Suspense,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
} from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { useSession } from "@/features/auth";
import type { SessionRole, SessionUser } from "@/features/auth/types";
import { lockAdminThemeDark, unlockAdminTheme } from "@/design/theme-core";
import { useUiStore } from "@/lib/store/ui-store";
import {
  adminGuardFor,
  type AdminGuardOutcome,
} from "@/lib/catalog-pending/admin/roleBoundary";
import { useOptionalQueryClient } from "@/lib/query";
import { AdminTopbar } from "./AdminTopbar";
import { AdminViewProvider, type AdminRole } from "./AdminViewContext";
import { useAdminShellWarmup } from "./adminRoutePrefetch";
import { useSidebarNotebookExpansion } from "./adminSidebarState";
import "./admin-shell.css";

// #232: route composition is eager, so a static sidebar import made every
// public/login visit parse the full backoffice navigation and icon catalog.
const LazyAdminSidebar = lazy(async () => {
  const sidebarModule = await import("./AdminSidebar");
  return { default: sidebarModule.AdminSidebar };
});

const ADMIN_SIDEBAR_FALLBACK = (
  <aside
    aria-busy="true"
    aria-label="Carregando menu administrativo"
    className="sticky top-0 hidden h-dvh w-[var(--admin-sidebar-width)] shrink-0 border-r border-border bg-card md:!flex"
    data-testid="admin-sidebar-loading"
  />
);

const ADMIN_LAYOUT_ROOT_CLASS_NAME =
  "admin-layout-root flex min-h-dvh min-w-0 bg-background text-foreground";

function BackgroundDecoration(): React.JSX.Element {
  return (
    <div
      className="pointer-events-none fixed inset-0 hidden lg:!block"
      data-testid="admin-background-decoration"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.16),_transparent_58%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(120deg,_rgba(6,35,39,0.86),_rgba(5,10,12,0.45)_54%,_rgba(0,0,0,0.18))]" />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,_rgba(255,255,255,0.025)_1px,_transparent_1px),linear-gradient(180deg,_rgba(255,255,255,0.025)_1px,_transparent_1px)] bg-[size:120px_120px] opacity-30" />
    </div>
  );
}

// Operator mandate (#215 follow-up): the backoffice keeps the legacy dark
// chrome ALWAYS. Lock the document to dark while any /admin route is mounted
// and restore the user's *resolved* preference (kept current in the ui-store
// by ThemeProvider) when the shell unmounts. The applyTheme() coercion in
// theme-core makes the lock win any race with the provider or a client toggle.
function useAdminForcedDarkTheme(): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    lockAdminThemeDark(root);
    return () => unlockAdminTheme(root, useUiStore.getState().theme);
  }, []);
}

interface AdminLayoutProps {
  /** When false the user is not logged in — redirect to /login. */
  isAuthenticated?: boolean | undefined;
  /** Test-only role override; runtime reads the session role. */
  isAdmin?: boolean | undefined;
}

// Resolve the effective session role. Runtime trusts `user.role` (server
// truth); the `isAdmin` test override only forces admin/user when no real
// role is present (issue #211: never infer role from email/matrixOnly).
function resolveRole(
  user: SessionUser | undefined,
  isAdmin: boolean | undefined,
): SessionRole {
  if (user?.role) return user.role;
  if (isAdmin !== undefined) return isAdmin ? "admin" : "user";
  return user?.is_admin === true ? "admin" : "user";
}

function toAdminViewRole(role: SessionRole): AdminRole {
  return role === "admin" || role === "support" ? role : "viewer";
}

// Translate a guard outcome into a Navigate target. `null` means render
// the layout in place. `/admin` is the admin-home fallback for a role
// that may use the shell but not this specific route.
function guardRedirectTarget(outcome: AdminGuardOutcome): string | null {
  switch (outcome) {
    case "render":
      return null;
    case "redirect_login":
      return "/login";
    case "redirect_root":
      return "/";
    case "redirect_matrix":
      return "/matrix";
    case "redirect_admin_home":
      return "/admin";
  }
}

export function AdminLayout({
  isAuthenticated,
  isAdmin,
}: AdminLayoutProps): React.JSX.Element {
  const access = useAdminLayoutAccess({ isAuthenticated, isAdmin });
  if (access.sessionLoading) {
    return <div className="min-h-dvh bg-card" aria-busy="true" />;
  }
  if (!access.authenticated) return <Navigate to="/login" replace />;
  if (access.redirect !== null) {
    return <Navigate to={access.redirect} replace />;
  }
  return <AdminLayoutShell pathname={access.pathname} role={access.role} />;
}

interface AdminLayoutAccess {
  readonly authenticated: boolean;
  readonly pathname: string;
  readonly redirect: string | null;
  readonly role: SessionRole;
  readonly sessionLoading: boolean;
}

function useAdminLayoutAccess(props: AdminLayoutProps): AdminLayoutAccess {
  const location = useLocation();
  const sessionState = useSession();
  useAdminForcedDarkTheme();
  const sessionLoading =
    props.isAuthenticated === undefined && sessionState.isLoading;
  const authenticated = props.isAuthenticated ?? sessionState.isAuthenticated;
  const role = resolveRole(sessionState.session?.user, props.isAdmin);
  const redirect = guardRedirectTarget(adminGuardFor(role, location.pathname));
  useAdminLayoutWarmup(!sessionLoading && authenticated && redirect === null);
  return {
    authenticated,
    pathname: location.pathname,
    redirect,
    role,
    sessionLoading,
  };
}

// Admin cache audit: warm dashboard KPIs, but never seed an auth error entry.
function useAdminLayoutWarmup(shellWillRender: boolean): void {
  const queryClient = useOptionalQueryClient();
  useAdminShellWarmup(shellWillRender ? queryClient : null);
}

interface AdminLayoutShellProps {
  readonly pathname: string;
  readonly role: SessionRole;
}

function AdminLayoutShell(props: AdminLayoutShellProps): React.JSX.Element {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notebookExpanded, toggleNotebook] = useSidebarNotebookExpansion();
  const openMenu = useCallback(() => setMobileOpen(true), []);
  const closeMenu = useCallback(() => setMobileOpen(false), []);
  useCloseMobileMenuAtPersistentBreakpoint(closeMenu);
  useEffect(() => setMobileOpen(false), [props.pathname]);
  return (
    <AdminViewProvider role={toAdminViewRole(props.role)}>
      <AdminShellFrame
        closeMenu={closeMenu}
        mobileOpen={mobileOpen}
        notebookExpanded={notebookExpanded}
        openMenu={openMenu}
        role={props.role}
        toggleNotebook={toggleNotebook}
      />
    </AdminViewProvider>
  );
}

interface AdminShellFrameProps {
  readonly closeMenu: () => void;
  readonly mobileOpen: boolean;
  readonly notebookExpanded: boolean;
  readonly openMenu: () => void;
  readonly role: SessionRole;
  readonly toggleNotebook: () => void;
}

function AdminShellFrame(props: AdminShellFrameProps): React.JSX.Element {
  return (
    <div
      className={ADMIN_LAYOUT_ROOT_CLASS_NAME}
      data-sidebar-notebook-expanded={String(props.notebookExpanded)}
      data-testid="admin-layout-root"
    >
      <AdminSidebarSuspense {...props} />
      <AdminMainContent onMenuClick={props.openMenu} />
    </div>
  );
}

function AdminSidebarSuspense(props: AdminShellFrameProps): React.JSX.Element {
  return (
    <Suspense fallback={ADMIN_SIDEBAR_FALLBACK}>
      <LazyAdminSidebar
        mobileOpen={props.mobileOpen}
        notebookExpanded={props.notebookExpanded}
        onClose={props.closeMenu}
        onToggleNotebook={props.toggleNotebook}
        matrixOnly={props.role === "matrix_user"}
        role={props.role}
      />
    </Suspense>
  );
}

function AdminMainContent(props: {
  readonly onMenuClick: () => void;
}): React.JSX.Element {
  // The sidebar is first in the flex flow, so its animated edge stays aligned.
  return (
    <div className="min-w-0 flex-1" data-testid="admin-layout-shell">
      <div className="relative min-h-dvh min-w-0 w-full">
        <BackgroundDecoration />
        <AdminTopbar onMenuClick={props.onMenuClick} />
        <main className="relative mx-auto min-w-0 max-w-[1440px] px-4 pb-6 pt-6 lg:px-10 lg:pb-16">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function useCloseMobileMenuAtPersistentBreakpoint(onClose: () => void): void {
  useEffect(() => {
    const breakpoint = window.matchMedia("(min-width: 768px)");
    const closeAtPersistentLayout = (event: MediaQueryListEvent): void => {
      if (event.matches) onClose();
    };
    if (breakpoint.matches) onClose();
    breakpoint.addEventListener("change", closeAtPersistentLayout);
    return () =>
      breakpoint.removeEventListener("change", closeAtPersistentLayout);
  }, [onClose]);
}
